import { marked } from "marked";
import type { TestCase } from "../api.js";

/**
 * A question as a student reads it: statement, then what to read, then what to
 * print, then a worked example.
 *
 * One component for both the practice workspace and the exam runner. The two
 * had drifted into rendering the description alone, which meant a question read
 * differently depending on where a student met it -- and in an exam that
 * difference is the one they cannot ask about.
 */
export default function ProblemStatement({
  description,
  inputFormat,
  outputFormat,
  testCases,
}: {
  description: string;
  inputFormat?: string;
  outputFormat?: string;
  /** Visible cases double as the worked example; hidden ones are never shown. */
  testCases?: TestCase[];
}) {
  const samples = (testCases ?? []).filter(
    (tc) => !tc.hidden && (tc.stdin || tc.expectedStdout)
  );

  return (
    <>
      <div
        className="desc"
        dangerouslySetInnerHTML={{
          __html: marked.parse(description || "_No description._") as string,
        }}
      />

      {inputFormat && <Section title="Input" body={inputFormat} />}
      {outputFormat && <Section title="Output" body={outputFormat} />}

      {samples.map((tc, i) => (
        <div key={tc.id} style={{ marginTop: 14 }}>
          <div className="desc">
            <h3 style={{ marginBottom: 6 }}>
              {samples.length === 1 ? "Example" : `Example ${i + 1}`}
            </h3>
          </div>
          <div className="sample">
            <div>
              <div className="sample-label">input</div>
              <pre>{tc.stdin || "(no input)"}</pre>
            </div>
            <div>
              <div className="sample-label">expected output</div>
              <pre>{tc.expectedStdout}</pre>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

function Section({ title, body }: { title: string; body: string }) {
  return (
    <div className="desc" style={{ marginTop: 14 }}>
      <h3>{title}</h3>
      <div dangerouslySetInnerHTML={{ __html: marked.parse(body) as string }} />
    </div>
  );
}
